import type { TFunction } from 'i18next';
import { formatCaseTreeLocationDisplay } from './caseLocationUtils';
import { formatCaseManagerOpenLabel } from './sampleStorageLinkHtml';

/** フォーム選択肢・i18n の正規コード */
export const STORAGE_ROOM_TYPE_CODES = [
  'ultra_low_temp_freezer',
  'refrigerator',
  'room_temp_storage',
  'dry_storage',
  'liquid_nitrogen_tank',
  'other',
] as const;

export type StorageRoomTypeCode = (typeof STORAGE_ROOM_TYPE_CODES)[number];

const CODE_SET = new Set<string>(STORAGE_ROOM_TYPE_CODES);

/** DB / シードの短縮コード → 正規コード */
const TYPE_ALIASES: Record<string, StorageRoomTypeCode> = {
  ultra_low: 'ultra_low_temp_freezer',
  ultra_low_temp: 'ultra_low_temp_freezer',
  ln2: 'liquid_nitrogen_tank',
  liquid_nitrogen: 'liquid_nitrogen_tank',
  room_temp: 'room_temp_storage',
  dry: 'dry_storage',
};

/**
 * 保管庫タイプ表示: コード・別名・既にローカライズ済みラベルを現在言語へ。
 */
export function formatStorageRoomTypeDisplay(
  raw: string | null | undefined,
  t: TFunction<'translation', undefined>
): string {
  if (raw == null || raw === '') return '-';
  const aliased = TYPE_ALIASES[raw] ?? raw;
  if (CODE_SET.has(aliased)) {
    return t(`storage_room.storage_types.${aliased}`);
  }
  for (const code of STORAGE_ROOM_TYPE_CODES) {
    if (t(`storage_room.storage_types.${code}`) === raw) return raw;
  }
  return raw;
}

/**
 * 保管室カード／モバイル facts 行。
 * 識別に効く場所・所有者を先に置き、タイプは末尾（名前に含まれるときは省略）。
 * 長い組織名は formatCaseTreeLocationDisplay で末尾優先。
 */
export function buildStorageRoomCardFacts(input: {
  roomName: string;
  typeDisplay: string;
  location: string;
  owner: string;
  /** 例:「12件」— 件数を場所・所有者の直後に置きスキャンしやすくする */
  sampleCountLabel?: string | null;
}): { display: string; title: string } {
  const name = input.roomName.trim();
  const type = input.typeDisplay.trim();
  const showType = Boolean(type && type !== '-' && !(name && name.includes(type)));
  const loc = formatCaseTreeLocationDisplay(input.location);
  const owner = input.owner.trim();
  const ownerBit = owner && owner !== '-' ? owner : null;
  const countBit = input.sampleCountLabel?.trim() || null;
  const typeBit = showType ? type : null;
  const locDisp = loc.display || null;
  const locFull = loc.full || null;
  return {
    display: [locDisp, ownerBit, countBit, typeBit].filter(Boolean).join(' · '),
    title: [locFull, ownerBit, countBit, typeBit].filter(Boolean).join(' · '),
  };
}

/**
 * 保管庫名リンクのケース導線。同名庫は所有者で区別（aria/title 共通 path）。
 * 短語＝aria、長語＝title（既存 chrome パターン）。
 */
export function formatStorageRoomOpenCaseLabels(
  roomName: string,
  owner: string,
  verbs: { short: string; full: string }
): { aria: string; title: string } {
  const name = roomName.trim() || '-';
  const ownerBit = owner.trim() && owner.trim() !== '-' ? owner.trim() : null;
  const path = [name, ownerBit].filter(Boolean).join(' · ');
  return {
    aria: formatCaseManagerOpenLabel(verbs.short, path),
    title: formatCaseManagerOpenLabel(verbs.full, path),
  };
}
